import * as React from 'react';
import { useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Collapse,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  NavItem,
  NavLink,
  UncontrolledDropdown,
} from 'reactstrap';
import 'core-js/stable/url-search-params';
import { API, Blank, hasPermissions, ROUTE_PATH } from '../constants';
import withAuthorization, { authorizedTo } from '../security/withAuthorization';
import Icon, { AWNLogo } from '../widgets/Icons';
import RouterNavLink from '../widgets/RouterNavLink';
import { useDispatch, useSelector } from 'react-redux';
import { sessionSelector } from '../reducers/session';
import { AllotmentUpdateModal } from './onboarding/OnboardingAllotmentUpdate';
import withFeatureFlagEnabled from '../security/withFeatureFlag';
import { useAuth0 } from '@auth0/auth0-react';
import { SESSION_ACTIONS } from '../actions/session';
import { PermissionEnum, ServicesEnum, SessionStorageKey } from '../types';
import styled from 'styled-components';
import { RolesAndServices } from '../selectors/selectRolesAndServices';
import { onboardingSelector } from '../reducers/onboarding';

type Props = {
  isAuth0Logout: boolean;
  rolesAndServices: RolesAndServices;
};

const Links = styled.a`
  color: inherit;
  text-decoration: none;
  display: inline-flex;
  width: 100%;
  :hover {
    text-decoration: none;
  }
`;

export const AppIconWrapper = styled(DropdownToggle)`
  color: #23618e;
  background-color: #23618e;
  border: none;
  box-shadow: none;

  :hover,
  :focus,
  :not(:disabled):not(.disabled):active:focus,
  :not(:disabled):not(.disabled):active {
    color: #23618e;
    background-color: #23618e;
    border: none;
    box-shadow: none;

    > svg {
      color: rgba(255, 255, 255, 0.5);
    }
  }
`;

const AppIcon = styled(Icon.Apps)`
  color: #fff;

  :hover {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const eitherMspOrAllCustomerSwitching = withAuthorization([
  'custswitch',
  'mspswitch',
]);

const DropdownMenuLabel = eitherMspOrAllCustomerSwitching(
  (props) => <span className="customer-name">{props.customerName}</span>,
  () => <span className="customer-name">Account</span>,
);

const DataDropdown: React.FunctionComponent = () => {
  return (
    <UncontrolledDropdown nav>
      <DropdownToggle nav>
        <div className="d-flex">
          Data
          <Icon.DownCaret className="ml-2" />
        </div>
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem tag={Link} to={ROUTE_PATH.DATA.FLOW}>
          Flow
        </DropdownItem>
        <DropdownItem tag={Link} to={ROUTE_PATH.DATA.AD}>
          Active Directory
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

const DashboardItem: React.FunctionComponent = () => (
  <NavItem>
    <RouterNavLink
      key={ROUTE_PATH.DASHBOARD.MAIN}
      to={ROUTE_PATH.DASHBOARD.MAIN}
    >
      Dashboard
    </RouterNavLink>
  </NavItem>
);

export const AppSwitcher: React.FunctionComponent<{
  rolesAndServices: RolesAndServices;
}> = ({ rolesAndServices }) => {
  const orgID = useSelector(sessionSelector.getRiskID);
  const customerID = useSelector(sessionSelector.getCustomerID);
  const orgIDParam = orgID ? `?org=${orgID}` : '';
  const { services } = rolesAndServices;
  const selections = [
    <DropdownItem key={ROUTE_PATH.DASHBOARD.UNIFIED}>
      <Links
        target="_blank"
        rel="noopener noreferrer"
        href={ROUTE_PATH.DASHBOARD.UNIFIED + orgIDParam}
      >
        Unified Portal
      </Links>
    </DropdownItem>,
  ];

  if (services.has(ServicesEnum.MR)) {
    const riskIDParam = orgID ? `?id=${orgID}` : '';
    selections.push(
      <DropdownItem key={ROUTE_PATH.DASHBOARD.RISK}>
        <Links
          target="_blank"
          rel="noopener noreferrer"
          href={ROUTE_PATH.DASHBOARD.RISK + riskIDParam}
          data-cy={ServicesEnum.MR}
        >
          Managed Risk
        </Links>
      </DropdownItem>,
    );
  }

  if (
    services.has(ServicesEnum.AWARENESS) ||
    services.has(ServicesEnum.AWARENESSPLUS)
  ) {
    const cidParam = customerID ? `?cid=${customerID}` : '';
    selections.push(
      <DropdownItem key={ROUTE_PATH.DASHBOARD.AWARENESS}>
        <Links
          target="_blank"
          rel="noopener noreferrer"
          href={ROUTE_PATH.DASHBOARD.AWARENESS + cidParam}
          data-cy={ServicesEnum.AWARENESS}
        >
          Managed Security Awareness
        </Links>
      </DropdownItem>,
    );
  }

  return (
    <UncontrolledDropdown>
      <AppIconWrapper>
        <AppIcon className="ml-2" />
      </AppIconWrapper>
      <DropdownMenu>
        <DropdownItem key="header" header data-cy="switch-to">
          Switch To
        </DropdownItem>
        {selections}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

const AllSwitchDropdownItem = authorizedTo.switchAllCustomers(
  DropdownItem,
  Blank,
);
const MSPSwitchDropdownItem = authorizedTo.switchMspOwnCustomers(
  DropdownItem,
  Blank,
);
const EitherSwitchDropdownItem = eitherMspOrAllCustomerSwitching(
  DropdownItem,
  Blank,
);

const VSensorDropDownItem = () => (
  <DropdownItem tag={Link} to={ROUTE_PATH.SELF_SERVICE.VIRTUAL}>
    Arctic Wolf Appliance Management
  </DropdownItem>
);

const DashboardDropdown: React.FunctionComponent<{
  rolesAndServices: RolesAndServices;
}> = ({ rolesAndServices }) => {
  const riskID = useSelector(sessionSelector.getRiskID);
  const customerID = useSelector(sessionSelector.getCustomerID);
  const { services } = rolesAndServices;
  if (services.has(ServicesEnum.MR) || services.has(ServicesEnum.AWARENESS)) {
    const selections = [];
    selections.push(
      <DropdownItem
        tag={Link}
        key={ROUTE_PATH.DASHBOARD.MAIN}
        to={ROUTE_PATH.DASHBOARD.MAIN}
      >
        Managed Detection and Response
      </DropdownItem>,
    );

    if (services.has(ServicesEnum.MR)) {
      const riskIDParam = riskID ? `?id=${riskID}` : '';
      selections.push(
        <DropdownItem key={ROUTE_PATH.DASHBOARD.RISK}>
          <Links
            target="_blank"
            rel="noopener noreferrer"
            href={ROUTE_PATH.DASHBOARD.RISK + riskIDParam}
            data-cy={ServicesEnum.MR}
          >
            Managed Risk
          </Links>
        </DropdownItem>,
      );
    }

    if (
      services.has(ServicesEnum.AWARENESS) ||
      services.has(ServicesEnum.AWARENESSPLUS)
    ) {
      const cidParam = customerID ? `?cid=${customerID}` : '';
      selections.push(
        <DropdownItem key={ROUTE_PATH.DASHBOARD.AWARENESS}>
          <Links
            target="_blank"
            rel="noopener noreferrer"
            href={`${ROUTE_PATH.DASHBOARD.AWARENESS}${cidParam}`}
            data-cy={ServicesEnum.AWARENESS}
          >
            Managed Security Awareness
          </Links>
        </DropdownItem>,
      );
    }

    return (
      <UncontrolledDropdown nav>
        <DropdownToggle nav>
          <div className="d-flex">
            Dashboard
            <Icon.DownCaret className="ml-2" />
          </div>
        </DropdownToggle>
        <DropdownMenu>{selections}</DropdownMenu>
      </UncontrolledDropdown>
    );
  } else {
    return <DashboardItem data-cy="dashboard-item" />;
  }
};

const DashboardNavItem = authorizedTo.viewDashboard(
  withFeatureFlagEnabled('additional_dashboard')(DashboardDropdown, () => (
    <DashboardItem />
  )),
  Blank,
);
const TicketNavItem = authorizedTo.viewTickets(NavItem, Blank);
const DataNavItemContent = withFeatureFlagEnabled('additional_data')(
  DataDropdown,
  () => (
    <NavItem>
      <RouterNavLink to={ROUTE_PATH.DATA.FLOW}>Data</RouterNavLink>
    </NavItem>
  ),
);
export const DataNavItem = (props: { rolesAndServices: RolesAndServices }) => {
  const { roles, services } = props['rolesAndServices'];
  if (hasPermissions(roles, services, PermissionEnum.VIEW_DATA)) {
    return <DataNavItemContent />;
  }
  return null;
};
const LogSearchNavItem = authorizedTo.viewRawLogs(NavItem, Blank);
const OnboardingNavItem = authorizedTo.viewOnboarding(NavItem, Blank);
const OnboardingFragment = authorizedTo.viewOnboarding(React.Fragment, Blank);
const ReportsNavItem = authorizedTo.viewReports(NavItem, Blank);

const MenuBar: React.FunctionComponent<Props> = (props) => {
  const { isAuth0Logout, rolesAndServices } = props;
  const { logout } = useAuth0();
  const history = useHistory();
  const dispatch = useDispatch();
  const isLegacyLoginPage = useSelector(sessionSelector.isLegacyLoginPage);
  const hasMDR = useSelector(onboardingSelector.isManagedDetectionEnabled);
  const hasMR = useSelector(onboardingSelector.isManagedRiskEnabled);
  const isSessionUserPrimaryOrSecondaryContact = useSelector(
    onboardingSelector.isSessionUserPrimaryOrSecondaryContact,
  );
  const orgID = useSelector(sessionSelector.getRiskID);
  const orgIDParam = orgID ? `?id=${orgID}` : '';
  const logoutCallback = useCallback(() => {
    const asyncAuth0Logout = async () => {
      await dispatch(
        SESSION_ACTIONS.LOGOUT('Successfully logged out.', 'success'),
      );
      logout({
        returnTo: `${window.location.origin}${
          isLegacyLoginPage ? '' : ROUTE_PATH.LOGIN
        }`,
        client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
      });
    };
    if (isAuth0Logout || !isLegacyLoginPage) {
      asyncAuth0Logout();
    } else {
      if (window.sessionStorage.getItem(SessionStorageKey.USE_AUTH0)) {
        history.push(API.logout);
      }
      window.sessionStorage.setItem(SessionStorageKey.LOGGED_OUT, 'true');
      dispatch(SESSION_ACTIONS.LOGOUT('Successfully logged out.', 'success'));
    }
  }, [logout, isAuth0Logout, history, dispatch, isLegacyLoginPage]);

  const customerName = useSelector(sessionSelector.getCustomerName);
  const username = useSelector(sessionSelector.getUsername);
  const isOnboarding = useSelector(sessionSelector.isCustomerOnboarding);
  const handoffComplete = useSelector(sessionSelector.isHandoffComplete);
  const isPartiallyComplete = useSelector(sessionSelector.isPartiallyComplete);
  const isReadyForHandoverOrAcceptance = useSelector(
    sessionSelector.isReadyForHandoverOrAcceptance,
  );
  const isAWN = useSelector(sessionSelector.isAwnUser);
  const customerPod = useSelector(sessionSelector.getCustomerPod);
  const [isOpen, setIsOpen] = React.useState(false);
  const toggle = React.useCallback(
    () => setIsOpen((wasOpen) => !wasOpen),
    [setIsOpen],
  );
  const loggedIn = username && customerName;

  const [allotmentIsOpen, setAllotmentIsOpen] = React.useState(false);
  const openAllotment = React.useCallback(
    () => setAllotmentIsOpen(true),
    [setAllotmentIsOpen],
  );
  const closeAllotment = React.useCallback(
    () => setAllotmentIsOpen(false),
    [setAllotmentIsOpen],
  );

  const showContactCST = React.useMemo(() => {
    return (
      handoffComplete || isPartiallyComplete || isReadyForHandoverOrAcceptance
    );
  }, [handoffComplete, isPartiallyComplete, isReadyForHandoverOrAcceptance]);

  const showContactOnboarding = React.useMemo(() => {
    return (
      isOnboarding || isPartiallyComplete || isReadyForHandoverOrAcceptance
    );
  }, [isOnboarding, isPartiallyComplete, isReadyForHandoverOrAcceptance]);

  return (
    <div className="top-navigation">
      <Navbar dark expand="md">
        {username && (
          <AppSwitcher
            data-cy="app-switcher"
            rolesAndServices={rolesAndServices}
          />
        )}
        <NavbarBrand href="/" className="prs-navbar-brand">
          <AWNLogo />
        </NavbarBrand>
        {loggedIn && <NavbarToggler onClick={toggle} />}
        {username && (
          <Collapse isOpen={isOpen} navbar>
            <Nav navbar className="flex-grow">
              {loggedIn && (
                <>
                  <OnboardingNavItem>
                    <RouterNavLink to={ROUTE_PATH.ONBOARDING.MAIN}>
                      Onboarding
                    </RouterNavLink>
                  </OnboardingNavItem>
                  <DashboardNavItem rolesAndServices={rolesAndServices} />
                  <DataNavItem rolesAndServices={rolesAndServices} />
                  <LogSearchNavItem>
                    <NavLink
                      href={`${ROUTE_PATH.DASHBOARD.UNIFIED}/data-explorer/raw-log/search${orgIDParam}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Logs
                    </NavLink>
                  </LogSearchNavItem>
                  <ReportsNavItem>
                    <NavLink
                      href={`${ROUTE_PATH.DASHBOARD.UNIFIED}/reports${orgIDParam}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Reports
                    </NavLink>
                  </ReportsNavItem>
                  <TicketNavItem>
                    <NavLink
                      href={`${ROUTE_PATH.DASHBOARD.UNIFIED}/tickets${orgIDParam}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Tickets
                    </NavLink>
                  </TicketNavItem>
                </>
              )}
            </Nav>
            <Nav navbar>
              {loggedIn && (
                <>
                  {showContactCST && (
                    <ContactNavLink to={ROUTE_PATH.CONTACT_CST}>
                      Contact Your CST
                    </ContactNavLink>
                  )}
                  {showContactOnboarding && (
                    <ContactNavLink to={ROUTE_PATH.CONTACT_ONBOARDING}>
                      Contact Onboarding
                    </ContactNavLink>
                  )}
                </>
              )}
              {customerPod && isAWN && (
                <NavItem data-cy="customer-pod">
                  <span className="nav-link">{customerPod}</span>
                </NavItem>
              )}
              <UncontrolledDropdown data-cy="user-menu" nav>
                <DropdownToggle nav data-cy="user-menu-toggle">
                  <div className="d-flex">
                    <Icon.User className="mr-2" />
                    <DropdownMenuLabel customerName={customerName} />
                    <Icon.DownCaret className="ml-2" />
                  </div>
                </DropdownToggle>
                <DropdownMenu>
                  <AllSwitchDropdownItem
                    tag={Link}
                    to={ROUTE_PATH.CUSTOMER_SWITCH}
                  >
                    Switch Customers
                  </AllSwitchDropdownItem>
                  {loggedIn && (
                    <>
                      <MSPSwitchDropdownItem
                        tag={Link}
                        to={ROUTE_PATH.MSP_DASHBOARD}
                      >
                        MSP Dashboard
                      </MSPSwitchDropdownItem>

                      <EitherSwitchDropdownItem divider />

                      <DropdownItem
                        tag={Link}
                        to={ROUTE_PATH.WHITELISTABLE_IPS}
                      >
                        Arctic Wolf IP Addresses
                      </DropdownItem>

                      {(hasMDR || hasMR) && (
                        <DropdownItem tag="li">
                          <Links
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`${ROUTE_PATH.DASHBOARD.UNIFIED}/${orgID}${ROUTE_PATH.CLOUD_SENSORS.MAIN}`}
                          >
                            Cloud Sensors
                          </Links>
                        </DropdownItem>
                      )}
                      {hasMR && (
                        <DropdownItem tag="li">
                          <Links
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`${ROUTE_PATH.DASHBOARD.UNIFIED}/${orgID}${ROUTE_PATH.CLOUD_SCANNERS.MAIN}`}
                          >
                            Cloud Scanners
                          </Links>
                        </DropdownItem>
                      )}
                      {isSessionUserPrimaryOrSecondaryContact && (
                        <DropdownItem tag="li">
                          <Links
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`${ROUTE_PATH.DASHBOARD.UNIFIED}/${orgID}${ROUTE_PATH.FEDERATED_AUTHENTICATION.UNIFIED_PORTAL}`}
                          >
                            Federated Authentication
                          </Links>
                        </DropdownItem>
                      )}

                      <DropdownItem tag="li">
                        <Links
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`${ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL}${orgIDParam}`}
                        >
                          Downloads
                        </Links>
                      </DropdownItem>

                      <VSensorDropDownItem />

                      <DropdownItem
                        tag={Link}
                        key={ROUTE_PATH.STATUS}
                        to={ROUTE_PATH.STATUS}
                      >
                        Status Page
                      </DropdownItem>

                      {isAWN && (
                        <OnboardingFragment>
                          <DropdownItem divider />
                          <DropdownItem header>
                            [Internal] Onboarding
                          </DropdownItem>
                          <DropdownItem onClick={openAllotment}>
                            Allotment Update
                          </DropdownItem>
                          <DropdownItem divider />
                        </OnboardingFragment>
                      )}
                    </>
                  )}
                  <DropdownItem data-cy="logout" onClick={logoutCallback}>
                    Log out {username}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Nav>
          </Collapse>
        )}
      </Navbar>
      {isAWN && (
        <AllotmentUpdateModal
          onCancel={closeAllotment}
          visible={allotmentIsOpen}
        />
      )}
    </div>
  );
};

export default MenuBar;

export const ContactNavLink = ({
  to,
  children,
}: {
  to: string;
  children: any;
}) => (
  <NavItem>
    <RouterNavLink to={to}>
      <span className="white-space-nowrap">
        <Icon.SpeechBubble className="mr-2 d-none d-md-inline mt-n1" />
        <span className="d-md-none d-lg-inline">{children}</span>
      </span>
    </RouterNavLink>
  </NavItem>
);
